/**
 * Review Center — split-view page for reviewing auto-created containers
 * and document assignments.
 *
 * Left panel: list of containers with pending review items
 * Right panel: detail tabs (Identification | Documents)
 */
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation, Trans } from "react-i18next";
import { AlertCircle, CheckCircle, File06, XCircle, Mail01, SearchLg, LinkExternal01, ArrowRight, Stars01 } from "@untitledui/icons";
import { Link, useSearchParams } from "react-router";
import {
    useReviewContainers,
    useReviewItems,
    useReviewItem,
    useResolveReviewItem,
    useDismissReviewItem,
} from "@/hooks/api/use-review-center";
import type { ReviewContainerSummary, ReviewItem, ReviewDocument, ReviewContainer, AgentQuestion } from "@/data/api/review-center";
import { ChevronDown, ChevronRight } from "@untitledui/icons";
import { MergeContainerModal } from "@/components/application/review-center/merge-container-modal";
import { toast } from "@/lib/toast-service";
import { formatDocumentType } from "@/utils/format-document-type";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import type { SelectItemType } from "@/components/base/select/select";
import { useDocumentPreview } from "@/components/application/ai-chat/hooks/use-document-preview";
import { EmailDetailPanel } from "@/components/application/trade-comms/email-detail-panel";
import { Plus, Trash01 } from "@untitledui/icons";
import { ShipperOnboardingHeaderAction } from "@/components/application/onboarding/shipper-onboarding-context";

type DetailTab = "identification" | "documents";

interface AdditionalContainer {
    booking_number: string;
    container_number: string;
    bl_number: string;
    phase: string;
}

const REVIEW_TYPE_LABELS: Record<string, string> = {
    container_identification: "Verify identification",
    document_assignment: "Assign documents",
    missing_initial_docs: "No documents",
    tracking_staleness: "Check tracking",
    missing_documents: "Missing documents",
};

const FIELD_LABELS: Record<string, string> = {
    estimated_arrival_date: "ETA",
    estimated_departure_date: "ETD",
    actual_arrival_date: "ATA",
    actual_departure_date: "ATD",
    vessel_name: "Vessel",
    voyage_number: "Voyage",
    container_type: "Type",
    container_size: "Size",
    carrier_name: "Carrier",
    carrier_scac_code: "SCAC",
    booking_number: "Booking",
    cargo_description: "Cargo",
    special_instructions: "Instructions",
    origin_port: "Origin",
    destination_port: "Destination",
    total_weight_kg: "Weight (kg)",
    total_packages: "Packages",
    consignee_name: "Consignee",
    shipper_name: "Shipper",
    status: "Status",
    requires_temperature_control: "Temp. controlled",
    service_contract_number: "Contract",
    purchase_order_number: "PO",
    shipper_company_id: "Shipper company",
    consignee_company_id: "Consignee company",
};

function humanizeFieldName(field: string): string {
    return FIELD_LABELS[field] || field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function confidenceDotColor(score: number | null): string {
    if (score === null) return "bg-gray-400";
    if (score < 0.4) return "bg-error-500";
    if (score < 0.7) return "bg-warning-500";
    return "bg-success-500";
}

function formatRelativeDate(dateStr: string): string {
    const now = Date.now();
    const diff = now - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const PHASE_ITEMS: SelectItemType[] = [
    { id: "draft", label: "Draft" },
    { id: "booked", label: "Booked" },
    { id: "in_transit", label: "In Transit" },
    { id: "arrived", label: "Arrived" },
    { id: "delivered", label: "Delivered" },
];

export function ReviewCenterPage() {
    const { t: tCoord } = useTranslation("pages:shipper-coordination");
    const [searchParams] = useSearchParams();
    const reviewItemIdFromUrl = searchParams.get("item");

    // State
    const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<DetailTab>("identification");
    const [showResolved, setShowResolved] = useState(false);
    const [mergeModalOpen, setMergeModalOpen] = useState(false);
    const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);

    // Editable fields for identification tab
    const [editFields, setEditFields] = useState<Record<string, string>>({});

    // Document decisions state
    const [docDecisions, setDocDecisions] = useState<Record<string, { action: "keep" | "reclassify" | "remove" | "move"; new_type?: string; move_to_container_id?: string }>>({});
    const [notes, setNotes] = useState("");

    // Additional containers for multi-container identification
    const [additionalContainers, setAdditionalContainers] = useState<AdditionalContainer[]>([]);

    // Agent question answers (EveMasterContainer)
    const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});

    // Collapsible sections (when agent questions present)
    const [identificationOpen, setIdentificationOpen] = useState(false);
    const [documentsOpen, setDocumentsOpen] = useState(false);

    // Email viewer panel (inline third column)
    const [emailDrawerMessageId, setEmailDrawerMessageId] = useState<string | null>(null);
    // Document preview panel (inline third column, mutually exclusive with email)
    const [documentDrawerDocId, setDocumentDrawerDocId] = useState<string | null>(null);

    // Resizable sidebar
    const [sidebarWidth, setSidebarWidth] = useState(280);
    // Resizable email panel
    const [emailPanelWidth, setEmailPanelWidth] = useState(400);
    // Resizable document panel
    const [docPanelWidth, setDocPanelWidth] = useState(420);

    const handleEmailResizeStart = useCallback(
        (e: React.MouseEvent) => {
            if (e.button !== 0) return;
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = emailPanelWidth;
            const handleMove = (ev: MouseEvent) => {
                // Dragging left = larger panel (startX - ev.clientX is positive)
                const newWidth = Math.min(700, Math.max(320, startWidth + startX - ev.clientX));
                setEmailPanelWidth(newWidth);
            };
            const handleUp = () => {
                document.removeEventListener("mousemove", handleMove);
                document.removeEventListener("mouseup", handleUp);
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            };
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
            document.addEventListener("mousemove", handleMove);
            document.addEventListener("mouseup", handleUp);
        },
        [emailPanelWidth],
    );

    const handleDocPanelResizeStart = useCallback(
        (e: React.MouseEvent) => {
            if (e.button !== 0) return;
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = docPanelWidth;
            const handleMove = (ev: MouseEvent) => {
                const newWidth = Math.min(700, Math.max(320, startWidth + startX - ev.clientX));
                setDocPanelWidth(newWidth);
            };
            const handleUp = () => {
                document.removeEventListener("mousemove", handleMove);
                document.removeEventListener("mouseup", handleUp);
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            };
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
            document.addEventListener("mousemove", handleMove);
            document.addEventListener("mouseup", handleUp);
        },
        [docPanelWidth],
    );

    // Open document preview (close email panel if open)
    const handleOpenDocPreview = useCallback((docId: string) => {
        setEmailDrawerMessageId(null);
        setDocumentDrawerDocId(docId);
    }, []);

    // Open email panel (close document panel if open)
    const handleOpenEmail = useCallback((messageId: string) => {
        setDocumentDrawerDocId(null);
        setEmailDrawerMessageId(messageId);
    }, []);

    const handleSidebarResizeStart = useCallback(
        (e: React.MouseEvent) => {
            if (e.button !== 0) return;
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = sidebarWidth;
            const handleMove = (ev: MouseEvent) => {
                const newWidth = Math.min(400, Math.max(240, startWidth + ev.clientX - startX));
                setSidebarWidth(newWidth);
            };
            const handleUp = () => {
                document.removeEventListener("mousemove", handleMove);
                document.removeEventListener("mouseup", handleUp);
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            };
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
            document.addEventListener("mousemove", handleMove);
            document.addEventListener("mouseup", handleUp);
        },
        [sidebarWidth],
    );

    // Queries
    const { data: containersData, isLoading: loadingContainers } = useReviewContainers({
        include_resolved: showResolved,
    });
    const { data: itemsData } = useReviewItems(
        selectedContainerId
            ? { container_id: selectedContainerId, status: showResolved ? undefined : "pending" }
            : undefined
    );

    // Find the best pending review item — prefer container_identification (has agent questions)
    const selectedItemId = useMemo(() => {
        if (!itemsData?.items?.length) return null;
        const idItem = itemsData.items.find((i) => i.review_type === "container_identification");
        return (idItem || itemsData.items[0])?.review_item_id || null;
    }, [itemsData]);

    const effectiveReviewItemId = reviewItemIdFromUrl || selectedItemId;

    const { data: detailData, isLoading: loadingDetail } = useReviewItem(effectiveReviewItemId);

    const deepLinkContainerApplied = useRef(false);
    useEffect(() => {
        deepLinkContainerApplied.current = false;
    }, [reviewItemIdFromUrl]);

    useEffect(() => {
        if (!reviewItemIdFromUrl || deepLinkContainerApplied.current) return;
        const cid = detailData?.review_item?.container_id;
        if (!cid) return;
        setSelectedContainerId(cid);
        deepLinkContainerApplied.current = true;
    }, [reviewItemIdFromUrl, detailData?.review_item?.container_id]);

    // Mutations
    const resolveMutation = useResolveReviewItem();
    const dismissMutation = useDismissReviewItem();

    const containers = containersData?.containers || [];
    const reviewItem = detailData?.review_item;
    const container = detailData?.container;
    const documents = detailData?.documents || [];
    const duplicateSuspects = detailData?.duplicate_suspects || [];

    // Reset edit state when container changes
    const handleSelectContainer = (id: string) => {
        setSelectedContainerId(id);
        setActiveTab("identification");
        setEditFields({});
        setDocDecisions({});
        setNotes("");
        setAdditionalContainers([]);
        setQuestionAnswers({});
        setIdentificationOpen(false);
        setDocumentsOpen(false);
    };

    // Handle identification resolve
    const handleConfirmIdentification = async () => {
        if (!effectiveReviewItemId) return;
        const corrections: Record<string, string | null> = {};
        if (editFields.booking_number !== undefined) corrections.booking_number = editFields.booking_number;
        if (editFields.container_number !== undefined) corrections.container_number = editFields.container_number;
        if (editFields.bl_number !== undefined) corrections.bl_number = editFields.bl_number;
        if (editFields.phase !== undefined) corrections.phase = editFields.phase;

        try {
            await resolveMutation.mutateAsync({
                id: effectiveReviewItemId,
                data: {
                    confirmed: true,
                    corrections: Object.keys(corrections).length > 0 ? corrections : undefined,
                    additional_containers: additionalContainers.length > 0
                        ? additionalContainers.filter((c) => c.booking_number || c.container_number)
                        : undefined,
                },
            });
            const count = additionalContainers.filter((c) => c.booking_number || c.container_number).length;
            toast.success(count > 0 ? `Resolved + ${count} container(s) created` : "Review item resolved");
            setEditFields({});
            setAdditionalContainers([]);
        } catch {
            toast.error("Failed to resolve review item");
        }
    };

    // Handle document resolve
    const handleConfirmDocuments = async () => {
        if (!effectiveReviewItemId) return;
        const decisions = Object.entries(docDecisions).map(([document_id, dec]) => ({
            document_id,
            ...dec,
        }));

        try {
            await resolveMutation.mutateAsync({
                id: effectiveReviewItemId,
                data: {
                    document_decisions: decisions.length > 0 ? decisions : undefined,
                    notes: notes || undefined,
                },
            });
            toast.success("Document review saved");
            setDocDecisions({});
            setNotes("");
        } catch {
            toast.error("Failed to save document review");
        }
    };

    // Handle agent question resolve
    const handleConfirmAgentQuestions = async () => {
        if (!effectiveReviewItemId) return;
        try {
            await resolveMutation.mutateAsync({
                id: effectiveReviewItemId,
                data: {
                    confirmed: true,
                    question_answers: Object.keys(questionAnswers).length > 0 ? questionAnswers : undefined,
                    notes: notes || undefined,
                },
            });
            toast.success("Review resolved");
            setQuestionAnswers({});
            setNotes("");
        } catch {
            toast.error("Failed to resolve review item");
        }
    };

    // Handle dismiss
    const handleDismiss = async () => {
        if (!effectiveReviewItemId) return;
        try {
            await dismissMutation.mutateAsync({ id: effectiveReviewItemId });
            toast.success("Review item dismissed");
        } catch {
            toast.error("Failed to dismiss");
        }
    };

    // Handle delete container
    const handleDeleteContainer = async () => {
        if (!effectiveReviewItemId) return;
        if (!confirm("Are you sure you want to archive this container?")) return;
        try {
            await resolveMutation.mutateAsync({
                id: effectiveReviewItemId,
                data: { delete_container: true },
            });
            toast.success("Container archived");
            setSelectedContainerId(null);
        } catch {
            toast.error("Failed to archive container");
        }
    };

    // Current review item type for tab display
    const currentItems = itemsData?.items || [];
    const identificationItem = currentItems.find((i) => i.review_type === "container_identification");

    // Check if agent questions are present (EveMasterContainer)
    const agentQuestions: AgentQuestion[] = detailData?.situation_context?.questions || [];
    const agentAnalysis = detailData?.situation_context?.agent_analysis || null;
    const hasAgentQuestions = agentQuestions.length > 0;

    return (
        <div className="flex h-[calc(100vh-6rem)] min-h-0 flex-col gap-4">
            <header className="shrink-0 space-y-2">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-2">
                        <h1 className="text-2xl font-semibold text-primary">{tCoord("reviewCenter.title")}</h1>
                        <p className="text-sm text-secondary">{tCoord("reviewCenter.intro")}</p>
                        <p className="text-sm text-tertiary">
                            <Trans
                                t={tCoord}
                                i18nKey="reviewCenter.vsIssues"
                                components={{
                                    issuesLink: (
                                        <Link
                                            to="/shipper/issues"
                                            className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
                                        />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                    <ShipperOnboardingHeaderAction />
                </div>
            </header>

            <div className="flex min-h-0 min-w-0 flex-1">
                {/* Left panel: Container list */}
                <div style={{ width: sidebarWidth }} className="shrink-0 overflow-y-auto rounded-xl border border-secondary bg-primary p-3">
                    <div className="mb-3 flex items-center justify-between gap-2">
                        <h2 className="text-sm font-semibold text-primary">
                            {loadingContainers
                                ? tCoord("reviewCenter.sidebarListTitleLoading")
                                : tCoord("reviewCenter.sidebarListTitle", { count: containers.length })}
                        </h2>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setShowResolved(!showResolved)}
                                className="text-xs text-tertiary hover:text-primary"
                            >
                                {showResolved ? "Hide resolved" : "Show resolved"}
                            </button>
                        </div>
                    </div>

                    {loadingContainers ? (
                        <div className="py-8 text-center text-sm text-tertiary">Loading...</div>
                    ) : containers.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 py-12 text-center">
                            <CheckCircle className="h-8 w-8 text-success-500" />
                            <p className="text-sm text-tertiary">All clear! No items to review.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {containers.map((c) => (
                                <ContainerCard
                                    key={c.container_id}
                                    container={c}
                                    selected={selectedContainerId === c.container_id}
                                    onClick={() => handleSelectContainer(c.container_id)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Resize handle */}
                <div
                    onMouseDown={handleSidebarResizeStart}
                    className="w-1 shrink-0 cursor-col-resize bg-transparent transition-colors hover:bg-brand-500/30"
                />

                {/* Center panel: Detail */}
                <div className="flex min-h-0 min-w-0 flex-1 flex-col rounded-xl border border-secondary bg-primary">
                    {!selectedContainerId ? (
                        <div className="flex flex-1 items-center justify-center text-sm text-tertiary">
                            Select a container to review
                        </div>
                    ) : loadingDetail ? (
                        <div className="flex flex-1 items-center justify-center text-sm text-tertiary">
                            Loading...
                        </div>
                    ) : (
                        <div className="flex min-h-0 flex-1 flex-col">
                            {/* Scrollable area: summary + tabs + content */}
                            <div className="min-h-0 flex-1 overflow-y-auto">
                                {/* Situation Summary */}
                                <SituationSummary
                                    containerSummary={containers.find((c) => c.container_id === selectedContainerId)}
                                    reviewItem={reviewItem}
                                    situationContext={detailData?.situation_context ?? null}
                                    onOpenEmail={handleOpenEmail}
                                />

                                {hasAgentQuestions ? (
                                    <>
                                        {/* Agent Analysis Section */}
                                        <AgentAnalysisSection
                                            analysis={agentAnalysis}
                                            questions={agentQuestions}
                                            answers={questionAnswers}
                                            onAnswer={(qId, optionId) =>
                                                setQuestionAnswers((prev) => ({ ...prev, [qId]: optionId }))
                                            }
                                            confidence={reviewItem?.confidence_score ?? null}
                                            jsoncargo={detailData?.situation_context?.jsoncargo_result ?? null}
                                        />

                                        {/* Collapsible: Identification */}
                                        <CollapsibleSection
                                            title="Container Details"
                                            open={identificationOpen}
                                            onToggle={() => setIdentificationOpen(!identificationOpen)}
                                        >
                                            <IdentificationTab
                                                container={container}
                                                reviewItem={identificationItem || reviewItem}
                                                editFields={editFields}
                                                setEditFields={setEditFields}
                                                duplicateSuspects={duplicateSuspects}
                                                onMerge={(targetId) => {
                                                    setMergeTargetId(targetId);
                                                    setMergeModalOpen(true);
                                                }}
                                                sourceEmailSubject={detailData?.source_email_subject ?? null}
                                                additionalContainers={additionalContainers}
                                                setAdditionalContainers={setAdditionalContainers}
                                                extractedRefs={detailData?.situation_context?.extracted_references ?? null}
                                            />
                                        </CollapsibleSection>

                                        {/* Collapsible: Documents */}
                                        {documents.length > 0 && (
                                            <CollapsibleSection
                                                title={`Documents (${documents.length})`}
                                                open={documentsOpen}
                                                onToggle={() => setDocumentsOpen(!documentsOpen)}
                                            >
                                                <DocumentsTab
                                                    documents={documents}
                                                    docDecisions={docDecisions}
                                                    setDocDecisions={setDocDecisions}
                                                    notes={notes}
                                                    setNotes={setNotes}
                                                    onOpenPreview={handleOpenDocPreview}
                                                    containerId={selectedContainerId ?? undefined}
                                                />
                                            </CollapsibleSection>
                                        )}

                                        {/* Notes */}
                                        <div className="px-4 pb-4">
                                            <label className="mb-1 block text-xs font-medium text-tertiary">Note (opzionale)</label>
                                            <textarea
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Aggiungi note..."
                                                rows={2}
                                                className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder:text-quaternary focus:border-brand-500 focus:outline-none"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* Guidance banner for classic view */}
                                        <ReviewGuidanceBanner reviewItem={reviewItem} />

                                        {/* Tabs (classic view — no agent questions) */}
                                        <div className="sticky top-0 z-10 flex border-b border-secondary bg-primary px-4">
                                            <TabButton
                                                active={activeTab === "identification"}
                                                onClick={() => setActiveTab("identification")}
                                            >
                                                Identification
                                            </TabButton>
                                            <TabButton
                                                active={activeTab === "documents"}
                                                onClick={() => setActiveTab("documents")}
                                                badge={documents.length > 0}
                                            >
                                                Documents{documents.length > 0 ? ` (${documents.length})` : ""}
                                            </TabButton>
                                        </div>

                                        {/* Tab content */}
                                        <div className="p-4">
                                            {activeTab === "identification" ? (
                                                <IdentificationTab
                                                    container={container}
                                                    reviewItem={identificationItem || reviewItem}
                                                    editFields={editFields}
                                                    setEditFields={setEditFields}
                                                    duplicateSuspects={duplicateSuspects}
                                                    onMerge={(targetId) => {
                                                        setMergeTargetId(targetId);
                                                        setMergeModalOpen(true);
                                                    }}
                                                    sourceEmailSubject={detailData?.source_email_subject ?? null}
                                                    additionalContainers={additionalContainers}
                                                    setAdditionalContainers={setAdditionalContainers}
                                                    extractedRefs={detailData?.situation_context?.extracted_references ?? null}
                                                />
                                            ) : (
                                                <DocumentsTab
                                                    documents={documents}
                                                    docDecisions={docDecisions}
                                                    setDocDecisions={setDocDecisions}
                                                    notes={notes}
                                                    setNotes={setNotes}
                                                    onOpenPreview={handleOpenDocPreview}
                                                    containerId={selectedContainerId ?? undefined}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Action buttons */}
                            <div className="flex items-center justify-between border-t border-secondary px-4 py-3">
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleDismiss}
                                        disabled={dismissMutation.isPending}
                                        className="rounded-lg border border-secondary px-3 py-1.5 text-sm text-secondary-foreground hover:bg-secondary"
                                    >
                                        Dismiss
                                    </button>
                                    <button
                                        onClick={handleDeleteContainer}
                                        className="rounded-lg border border-error-300 px-3 py-1.5 text-sm text-error-600 hover:bg-error-50"
                                    >
                                        Delete container
                                    </button>
                                </div>
                                <button
                                    onClick={
                                        hasAgentQuestions
                                            ? handleConfirmAgentQuestions
                                            : activeTab === "identification"
                                                ? handleConfirmIdentification
                                                : handleConfirmDocuments
                                    }
                                    disabled={resolveMutation.isPending}
                                    className="rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                                >
                                    {resolveMutation.isPending ? "Saving..." : "Confirm & Save"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Merge modal */}
                {mergeModalOpen && selectedContainerId && mergeTargetId && (
                    <MergeContainerModal
                        isOpen={mergeModalOpen}
                        onClose={() => setMergeModalOpen(false)}
                        sourceId={selectedContainerId}
                        targetId={mergeTargetId}
                        sourceLabel={container?.container_number || container?.booking_number || selectedContainerId?.slice(0, 8) || "?"}
                        targetLabel={(() => {
                            const t = duplicateSuspects.find((s) => s.container_id === mergeTargetId);
                            return t?.container_number || t?.booking_number || mergeTargetId?.slice(0, 8) || "?";
                        })()}
                    />
                )}

                {/* Email panel — inline third column with resize handle */}
                {emailDrawerMessageId && (
                    <>
                        <div
                            onMouseDown={handleEmailResizeStart}
                            className="w-1 shrink-0 cursor-col-resize bg-transparent transition-colors hover:bg-brand-500/30"
                        />
                        <div style={{ width: emailPanelWidth }} className="flex shrink-0 animate-in slide-in-from-right flex-col rounded-xl border border-secondary bg-primary duration-200">
                            <div className="flex items-center justify-between border-b border-secondary px-4 py-2">
                                <h3 className="text-sm font-semibold text-primary">Source Email</h3>
                                <button
                                    onClick={() => setEmailDrawerMessageId(null)}
                                    className="rounded p-1 text-quaternary transition-colors hover:text-primary"
                                >
                                    <XCircle className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="min-h-0 flex-1 overflow-y-auto">
                                <EmailDetailPanel
                                    messageId={emailDrawerMessageId}
                                    onCloseDetail={() => setEmailDrawerMessageId(null)}
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Document preview panel — inline third column with resize handle */}
                {documentDrawerDocId && !emailDrawerMessageId && (
                    <>
                        <div
                            onMouseDown={handleDocPanelResizeStart}
                            className="w-1 shrink-0 cursor-col-resize bg-transparent transition-colors hover:bg-brand-500/30"
                        />
                        <div style={{ width: docPanelWidth }} className="flex shrink-0 animate-in slide-in-from-right flex-col rounded-xl border border-secondary bg-primary duration-200">
                            <DocumentDetailPanel
                                documentId={documentDrawerDocId}
                                onClose={() => setDocumentDrawerDocId(null)}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Agent Analysis & Collapsible Sections
// ============================================================================

function AgentAnalysisSection({
    analysis,
    questions,
    answers,
    onAnswer,
    confidence,
    jsoncargo,
}: {
    analysis: string | null;
    questions: AgentQuestion[];
    answers: Record<string, string>;
    onAnswer: (questionId: string, optionId: string) => void;
    confidence: number | null;
    jsoncargo: Record<string, unknown> | null;
}) {
    return (
        <div className="border-b border-secondary p-4">
            {/* Analysis summary */}
            {analysis && (
                <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50 p-3 dark:border-brand-800 dark:bg-brand-950">
                    <div className="mb-2 flex items-center gap-2">
                        <SearchLg className="h-4 w-4 text-brand-600" />
                        <span className="text-xs font-semibold text-brand-700 dark:text-brand-400">Analisi Agente</span>
                        {confidence !== null && (
                            <span className="ml-auto flex items-center gap-1 text-xs text-tertiary">
                                <span className={`inline-block h-2 w-2 rounded-full ${confidenceDotColor(confidence)}`} />
                                {Math.round(confidence * 100)}%
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-secondary-foreground whitespace-pre-wrap">{analysis}</p>
                </div>
            )}

            {/* JSONCargo result */}
            {jsoncargo && !!(jsoncargo as Record<string, unknown>).found && (
                <div className="mb-4 rounded-lg border border-success-200 bg-success-50 p-3 dark:border-success-800 dark:bg-success-950">
                    <p className="text-xs font-medium text-success-700 dark:text-success-400">
                        Verificato dal carrier: {String((jsoncargo as Record<string, unknown>).carrier || "")} — Status: {String((jsoncargo as Record<string, unknown>).status || "unknown")}
                        {!!(jsoncargo as Record<string, unknown>).vessel && ` — Nave: ${String((jsoncargo as Record<string, unknown>).vessel)}`}
                    </p>
                </div>
            )}

            {/* Questions */}
            {questions.map((q) => (
                <div key={q.id} className="mb-4">
                    <p className="mb-2 text-sm font-medium text-primary">{q.question}</p>
                    <div className="flex flex-col gap-1.5">
                        {q.options.map((opt) => (
                            <label
                                key={opt.id}
                                className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors ${answers[q.id] === opt.id
                                        ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300"
                                        : "border-secondary text-secondary-foreground hover:border-tertiary hover:bg-secondary"
                                    }`}
                            >
                                <input
                                    type="radio"
                                    name={`agent-q-${q.id}`}
                                    value={opt.id}
                                    checked={answers[q.id] === opt.id}
                                    onChange={() => onAnswer(q.id, opt.id)}
                                    className="accent-brand-600"
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function CollapsibleSection({
    title,
    open,
    onToggle,
    children,
}: {
    title: string;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="border-b border-secondary">
            <button
                onClick={onToggle}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-secondary-foreground hover:bg-secondary"
            >
                {open ? (
                    <ChevronDown className="h-4 w-4 text-tertiary" />
                ) : (
                    <ChevronRight className="h-4 w-4 text-tertiary" />
                )}
                {title}
            </button>
            {open && <div className="px-4 pb-4">{children}</div>}
        </div>
    );
}

function ReviewGuidanceBanner({ reviewItem }: { reviewItem: ReviewItem | undefined }) {
    if (!reviewItem) return null;

    const type = reviewItem.review_type;
    const confidence = reviewItem.confidence_score;
    const ctx = reviewItem.context as Record<string, unknown> | null;
    const source = reviewItem.source;

    let title = "";
    let description = "";

    if (type === "container_identification") {
        title = "Verify container identification";
        if (source === "coherence_guard" && ctx?.reason === "status_date_mismatch") {
            description = `Status "${ctx.attempted_status}" was blocked because ${ctx.fail_reason}. Current status: "${ctx.current_status}". Review the container's actual status and update manually if needed.`;
        } else if (source === "periodic_scan" && ctx?.reason === "draft_stale") {
            description = "This container has been in DRAFT for a while. Verify that the booking number and container number are correct, or delete it if it's not needed.";
        } else if (source === "periodic_scan" && ctx?.reason === "no_documents") {
            description = "No documents have been linked to this container. Check if documents are missing or if the container was created by mistake.";
        } else if (confidence !== null && confidence < 0.5) {
            description = "This container was auto-created with low confidence. Please verify the booking/container numbers are correct and the references match.";
        } else {
            description = "Review the container details below. Correct any wrong references, merge with duplicates, or add missing containers.";
        }
    } else if (type === "document_assignment") {
        if (ctx?.reason === "no_documents") {
            title = "No documents linked";
            description = "This container has been active but has no documents. Upload documents, link existing emails, or dismiss if documents are not expected yet.";
        } else {
            title = "Review document assignment";
            description = "Check that these documents are linked to the correct container. You can reclassify, remove, or move documents to another container.";
        }
    } else if (type === "tracking_staleness") {
        title = "Tracking data may be outdated";
        description = "The tracking data for this container hasn't been updated recently. Verify the container status is still accurate.";
    } else if (type === "missing_initial_docs") {
        title = "No documents linked";
        description = "This container has been active for over 24 hours but has no documents. Upload documents, link existing emails, or dismiss if documents are not expected yet.";
    } else if (type === "missing_documents") {
        title = "Missing required documents";
        description = "One or more required documents are missing for this container's current phase.";
    }

    if (!title) return null;

    return (
        <div className="border-b border-secondary bg-secondary/20 px-4 py-3">
            <p className="text-sm font-medium text-primary">{title}</p>
            <p className="mt-0.5 text-xs text-tertiary">{description}</p>
        </div>
    );
}

// ============================================================================
// Sub-components
// ============================================================================

function ContainerCard({
    container,
    selected,
    onClick,
}: {
    container: ReviewContainerSummary;
    selected: boolean;
    onClick: () => void;
}) {
    const primaryLabel = container.container_number
        ? container.container_number
        : container.booking_number
            ? `Booking ${container.booking_number}`
            : "Unidentified container";

    const secondaryLabel = container.container_number
        ? container.booking_number
            ? `Booking: ${container.booking_number}`
            : "No booking"
        : container.booking_number
            ? "No container number"
            : container.created_at
                ? `Created ${new Date(container.created_at).toLocaleDateString()}`
                : null;

    const sourceHints = container.source_hints || [];
    const docCount = container.document_count || 0;

    // Build a short, clear reason line for the card
    const types = container.review_types || [];
    const hasIdReview = types.includes("container_identification");
    const hasDocReview = types.includes("document_assignment");
    const hasMissingInitialDocs = types.includes("missing_initial_docs");
    let reasonLine = "";
    if (hasIdReview && hasDocReview) {
        reasonLine = "Verify details and assign documents";
    } else if (hasDocReview) {
        reasonLine = "No documents linked yet";
    } else if (hasMissingInitialDocs) {
        reasonLine = "No documents linked yet";
    } else if (hasIdReview) {
        reasonLine = "Verify booking/container details";
    } else {
        reasonLine = types.map((t) => REVIEW_TYPE_LABELS[t] || t.replace(/_/g, " ")).join(" \u00B7 ");
    }

    return (
        <button
            onClick={onClick}
            className={`w-full rounded-lg border p-3 text-left transition-colors ${selected
                    ? "border-brand-500 bg-brand-500/10 ring-1 ring-brand-500"
                    : "border-secondary hover:bg-secondary/50"
                }`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span
                        className={`inline-block h-2 w-2 shrink-0 rounded-full ${confidenceDotColor(container.lowest_confidence)}`}
                        title={container.lowest_confidence !== null ? `Confidence: ${Math.round(container.lowest_confidence * 100)}%` : "Unknown confidence"}
                    />
                    <span className="truncate text-sm font-medium text-primary">
                        {primaryLabel}
                    </span>
                    {container.pending_review_count === 0 && (
                        <span className="shrink-0 rounded bg-success-100 px-1.5 py-0.5 text-[10px] font-medium text-success-700">
                            Resolved
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {(container.review_types || []).includes("container_identification") && (
                        <span className="text-[10px] text-brand-500" title="Agent analysis available">
                            <Stars01 className="h-3 w-3" />
                        </span>
                    )}
                    {docCount > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-quaternary" title={`${docCount} document(s)`}>
                            <File06 className="h-3 w-3" />
                            {docCount}
                        </span>
                    )}
                    {container.created_at && (
                        <span className="text-[10px] text-quaternary" title={new Date(container.created_at).toLocaleString()}>
                            {formatRelativeDate(container.created_at)}
                        </span>
                    )}
                </div>
            </div>

            {secondaryLabel && (
                <div className="mt-0.5 pl-3.5 text-xs text-tertiary">{secondaryLabel}</div>
            )}

            {reasonLine && (
                <div className="mt-1 pl-3.5 text-xs text-quaternary">{reasonLine}</div>
            )}

            {sourceHints.length > 0 ? (
                <div className="mt-2 border-t border-secondary/50 pt-2 space-y-0.5">
                    {sourceHints.slice(0, 2).map((hint, i) => (
                        <Link
                            key={i}
                            to={hint.type === "email"
                                ? `/shipper/activity?tab=trade-comms&messageId=${hint.id}`
                                : `/shipper/documents/${hint.id}`
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 truncate pl-0.5 text-xs text-tertiary hover:text-brand-600"
                        >
                            {hint.type === "email" ? (
                                <Mail01 className="h-3 w-3 shrink-0 text-quaternary" />
                            ) : (
                                <File06 className="h-3 w-3 shrink-0 text-quaternary" />
                            )}
                            <span className="truncate">{hint.label}</span>
                        </Link>
                    ))}
                    {sourceHints.length > 2 && (
                        <div className="pl-4 text-xs text-quaternary">+{sourceHints.length - 2} more</div>
                    )}
                </div>
            ) : null}
        </button>
    );
}

function TabButton({
    active,
    onClick,
    badge,
    children,
}: {
    active: boolean;
    onClick: () => void;
    badge?: boolean;
    children: React.ReactNode;
}) {
    return (
        <button
            onClick={onClick}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${active
                    ? "border-b-2 border-brand-600 text-brand-700"
                    : "text-tertiary hover:text-primary"
                }`}
        >
            {children}
            {badge && (
                <span className="absolute right-1 top-2 h-1.5 w-1.5 rounded-full bg-error-500" />
            )}
        </button>
    );
}

function IdentificationTab({
    container,
    reviewItem,
    editFields,
    setEditFields,
    duplicateSuspects,
    onMerge,
    sourceEmailSubject,
    additionalContainers,
    setAdditionalContainers,
    extractedRefs,
}: {
    container: ReviewContainer | null | undefined;
    reviewItem: ReviewItem | null | undefined;
    editFields: Record<string, string>;
    setEditFields: (fields: Record<string, string>) => void;
    duplicateSuspects: ReviewContainer[];
    onMerge: (targetId: string) => void;
    sourceEmailSubject: string | null;
    additionalContainers: AdditionalContainer[];
    setAdditionalContainers: (c: AdditionalContainer[]) => void;
    extractedRefs: import("@/data/api/review-center").ExtractedReferences | null;
}) {
    const getField = (key: string) =>
        editFields[key] ?? (container as unknown as Record<string, unknown>)?.[key] ?? "";

    const setField = (key: string, value: string) =>
        setEditFields({ ...editFields, [key]: value });

    const updateAdditional = (index: number, field: keyof AdditionalContainer, value: string) => {
        const updated = [...additionalContainers];
        updated[index] = { ...updated[index], [field]: value };
        setAdditionalContainers(updated);
    };

    const addContainer = (prefill?: Partial<AdditionalContainer>) => {
        setAdditionalContainers([
            ...additionalContainers,
            { booking_number: "", container_number: "", bl_number: "", phase: "draft", ...prefill },
        ]);
    };

    const removeContainer = (index: number) => {
        setAdditionalContainers(additionalContainers.filter((_, i) => i !== index));
    };

    // Check if there are unassigned extracted references we can suggest
    const mainBooking = String(getField("booking_number"));
    const mainContainer = String(getField("container_number"));
    const usedBookings = new Set([mainBooking, ...additionalContainers.map((c) => c.booking_number)].filter(Boolean));
    const suggestedBookings = (extractedRefs?.booking_numbers || []).filter((b) => !usedBookings.has(b));
    const usedContainers = new Set([mainContainer, ...additionalContainers.map((c) => c.container_number)].filter(Boolean));
    const suggestedContainers = (extractedRefs?.container_numbers || []).filter((c) => !usedContainers.has(c));

    return (
        <div className="space-y-6">
            {/* Main container fields */}
            <div>
                <h4 className="mb-2 text-xs font-semibold uppercase text-tertiary">Container 1 (existing)</h4>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <FieldInput label="Booking Number" value={String(getField("booking_number"))} onChange={(v) => setField("booking_number", v)} />
                        <SourceLine reviewItem={reviewItem} sourceEmailSubject={sourceEmailSubject} />
                    </div>
                    <div>
                        <FieldInput label="Container Number" value={String(getField("container_number"))} onChange={(v) => setField("container_number", v)} />
                        <SourceLine reviewItem={reviewItem} sourceEmailSubject={sourceEmailSubject} />
                    </div>
                    <div>
                        <FieldInput label="BL Number" value={String(getField("bl_number"))} onChange={(v) => setField("bl_number", v)} />
                        <SourceLine reviewItem={reviewItem} sourceEmailSubject={sourceEmailSubject} />
                    </div>
                    <Select
                        label="Phase"
                        items={PHASE_ITEMS}
                        selectedKey={editFields.phase ?? container?.status ?? "draft"}
                        onSelectionChange={(key) => setField("phase", String(key))}
                        size="sm"
                    >
                        {(item) => <SelectItem {...item} />}
                    </Select>
                </div>
            </div>

            {/* Additional containers */}
            {additionalContainers.map((ac, idx) => (
                <div key={idx} className="rounded-lg border border-dashed border-secondary p-3">
                    <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-xs font-semibold uppercase text-tertiary">Container {idx + 2} (new)</h4>
                        <button onClick={() => removeContainer(idx)} className="rounded p-1 text-quaternary hover:text-error-500" title="Remove">
                            <Trash01 className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldInput label="Booking Number" value={ac.booking_number} onChange={(v) => updateAdditional(idx, "booking_number", v)} />
                        <FieldInput label="Container Number" value={ac.container_number} onChange={(v) => updateAdditional(idx, "container_number", v)} />
                        <FieldInput label="BL Number" value={ac.bl_number} onChange={(v) => updateAdditional(idx, "bl_number", v)} />
                        <Select
                            label="Phase"
                            items={PHASE_ITEMS}
                            selectedKey={ac.phase || "draft"}
                            onSelectionChange={(key) => updateAdditional(idx, "phase", String(key))}
                            size="sm"
                        >
                            {(item) => <SelectItem {...item} />}
                        </Select>
                    </div>
                </div>
            ))}

            {/* Suggestions from extracted references */}
            {(suggestedBookings.length > 0 || suggestedContainers.length > 0) && additionalContainers.length === 0 && (
                <div className="rounded-lg border border-secondary bg-secondary/30 p-3">
                    <p className="mb-2 text-xs font-medium text-secondary-foreground">
                        Found {suggestedBookings.length + suggestedContainers.length} more reference(s) in the email. Add them?
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {suggestedBookings.map((b) => (
                            <button
                                key={b}
                                onClick={() => addContainer({ booking_number: b })}
                                className="rounded-md border border-secondary bg-primary px-2.5 py-1 text-xs font-medium text-primary hover:bg-secondary"
                            >
                                + {b}
                            </button>
                        ))}
                        {suggestedContainers.map((c) => (
                            <button
                                key={c}
                                onClick={() => addContainer({ container_number: c })}
                                className="rounded-md border border-secondary bg-primary px-2.5 py-1 text-xs font-medium text-primary hover:bg-secondary"
                            >
                                + {c}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Add container button */}
            <button
                onClick={() => addContainer()}
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-secondary px-3 py-2 text-xs text-tertiary hover:border-brand-500 hover:text-brand-600"
            >
                <Plus className="h-3.5 w-3.5" />
                Add another container
            </button>

            {/* Duplicate suspects */}
            {duplicateSuspects.length > 0 && (
                <div className="rounded-lg border border-secondary bg-secondary/30 p-3">
                    <h4 className="mb-2 text-xs font-medium text-secondary-foreground">Possible duplicates</h4>
                    <div className="space-y-1.5">
                        {duplicateSuspects.map((s) => (
                            <div key={s.container_id} className="flex items-center justify-between rounded-md border border-secondary bg-primary px-3 py-2">
                                <div className="text-xs text-primary">
                                    <span className="font-medium">{s.container_number || "???"}</span>
                                    <span className="text-tertiary">{" — BK: "}{s.booking_number || "—"}</span>
                                    <span className="text-tertiary">{" — BL: "}{s.bl_number || "—"}</span>
                                </div>
                                <button
                                    onClick={() => onMerge(s.container_id)}
                                    className="rounded-md border border-secondary px-2 py-1 text-xs font-medium text-secondary-foreground hover:bg-secondary"
                                >
                                    View & Merge
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function DocumentsTab({
    documents,
    docDecisions,
    setDocDecisions,
    notes,
    setNotes,
    onOpenPreview,
    containerId,
}: {
    documents: ReviewDocument[];
    docDecisions: Record<string, { action: "keep" | "reclassify" | "remove" | "move"; new_type?: string; move_to_container_id?: string }>;
    setDocDecisions: (d: Record<string, { action: "keep" | "reclassify" | "remove" | "move"; new_type?: string; move_to_container_id?: string }>) => void;
    notes: string;
    setNotes: (n: string) => void;
    onOpenPreview?: (docId: string) => void;
    containerId?: string;
}) {
    return (
        <div className="space-y-4">
            {documents.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <File06 className="h-8 w-8 text-tertiary" />
                    <p className="text-sm text-tertiary">No documents linked to this container</p>
                    {containerId && (
                        <a
                            href={`/shipper/containers/${containerId}?tab=documents`}
                            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 transition-colors"
                        >
                            Upload documents
                        </a>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    {documents.map((doc) => (
                        <DocCard
                            key={doc.document_id}
                            doc={doc}
                            decision={docDecisions[doc.document_id]?.action || "keep"}
                            onDecision={(action) =>
                                setDocDecisions({ ...docDecisions, [doc.document_id]: { ...docDecisions[doc.document_id], action } })
                            }
                            onOpenPreview={onOpenPreview}
                            moveToContainerId={docDecisions[doc.document_id]?.move_to_container_id}
                            onMoveTargetChange={(containerId) =>
                                setDocDecisions({ ...docDecisions, [doc.document_id]: { ...docDecisions[doc.document_id], action: "move", move_to_container_id: containerId } })
                            }
                        />
                    ))}
                </div>
            )}

            {/* Notes */}
            <div>
                <label className="mb-1 block text-xs font-medium text-secondary-foreground">Notes</label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Add notes about this review..."
                    className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder:text-quaternary"
                />
            </div>
        </div>
    );
}

function FieldInput({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
}) {
    return (
        <div>
            <label className="mb-1 block text-xs font-medium text-secondary-foreground">{label}</label>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary"
            />
        </div>
    );
}

function SourceLine({ reviewItem, sourceEmailSubject }: { reviewItem: ReviewItem | null | undefined; sourceEmailSubject: string | null }) {
    if (!reviewItem) return null;
    const ctx = reviewItem.context as Record<string, unknown> | null;
    if (!ctx) return null;

    const emailId = ctx.source_email_id as string | undefined;
    const docId = ctx.document_id as string | undefined;
    const docType = (ctx.document_type as string)?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());

    if (emailId && sourceEmailSubject) {
        return (
            <Link
                to={`/shipper/activity?tab=trade-comms&messageId=${emailId}`}
                className="mt-1 flex items-center gap-1 text-xs text-tertiary hover:text-brand-600"
            >
                <Mail01 className="h-3 w-3 shrink-0" />
                <span className="truncate">from &quot;{sourceEmailSubject}&quot;</span>
                <span className="shrink-0">&rarr;</span>
            </Link>
        );
    }

    if (docId) {
        return (
            <Link
                to={`/shipper/documents/${docId}`}
                className="mt-1 flex items-center gap-1 text-xs text-tertiary hover:text-brand-600"
            >
                <File06 className="h-3 w-3 shrink-0" />
                <span className="truncate">from {docType || "Document"}</span>
                <span className="shrink-0">&rarr;</span>
            </Link>
        );
    }

    return null;
}

function SituationSummary({
    containerSummary,
    reviewItem,
    situationContext,
    onOpenEmail,
}: {
    containerSummary: ReviewContainerSummary | undefined;
    reviewItem: ReviewItem | null | undefined;
    situationContext: import("@/data/api/review-center").SituationContext | null;
    onOpenEmail: (messageId: string) => void;
}) {
    const sc = situationContext;
    const sourceHints = containerSummary?.source_hints || [];
    const emailHint = sourceHints.find((h) => h.type === "email");
    // Email info — prefer situation_context, fallback to source_hints
    const emailSubject = sc?.email_subject || emailHint?.label || null;
    const emailFrom = sc?.email_from || null;
    const emailReceivedAt = sc?.email_received_at ? new Date(sc.email_received_at) : null;
    const emailSummary = sc?.email_summary || null;
    const attentionReasons = sc?.attention_reasons || [];
    const systemActions = sc?.system_actions || [];
    const refs = sc?.extracted_references || null;
    const creation = sc?.container_creation || null;

    // Find the email ID for linking
    const ctx = reviewItem?.context as Record<string, unknown> | null;
    const emailId = (ctx?.source_email_id as string) || emailHint?.id || null;

    const [detailsOpen, setDetailsOpen] = useState(false);
    const hasDetails = attentionReasons.length > 0 || systemActions.length > 0 || (refs && (refs.booking_numbers.length > 0 || refs.container_numbers.length > 0 || refs.bl_numbers.length > 0));

    return (
        <div className="border-b border-secondary bg-secondary/20 px-4 py-4 space-y-3">
            {/* Email summary — always visible */}
            {emailSummary && (
                <p className="text-sm leading-relaxed text-primary">{emailSummary}</p>
            )}

            {/* Fallback: if no email summary, show template reasons */}
            {!emailSummary && attentionReasons.length === 0 && (
                <div className="space-y-1">
                    {((containerSummary?.review_types || []).includes("document_assignment") ||
                        (containerSummary?.review_types || []).includes("missing_initial_docs")) && (
                            <div className="flex items-start gap-2">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" />
                                <p className="text-sm text-primary">No documents have been linked to this container yet.</p>
                            </div>
                        )}
                    {(containerSummary?.review_types || []).includes("container_identification") && (
                        <div className="flex items-start gap-2">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" />
                            <p className="text-sm text-primary">Container identification needs verification.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Source email box — always visible, click to open drawer */}
            {emailSubject && (
                <button
                    onClick={() => emailId && onOpenEmail(emailId)}
                    className="w-full rounded-md bg-secondary/40 px-3 py-2 text-left transition-colors hover:bg-secondary/60"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                            <Mail01 className="h-3.5 w-3.5 text-quaternary" />
                            <p className="text-xs font-medium text-secondary-foreground">Source email</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {emailReceivedAt && (
                                <span className="text-xs text-quaternary">
                                    {emailReceivedAt.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                                </span>
                            )}
                            {emailId && (
                                <span className="text-xs text-brand-600">View &rarr;</span>
                            )}
                        </div>
                    </div>
                    <div className="mt-1 space-y-0.5">
                        <p className="text-sm text-primary">{emailSubject}</p>
                        {emailFrom && (
                            <p className="text-xs text-tertiary">From: {emailFrom}</p>
                        )}
                    </div>
                </button>
            )}

            {/* System details — toggle, closed by default */}
            {hasDetails && (
                <div>
                    <button
                        onClick={() => setDetailsOpen(!detailsOpen)}
                        className="flex w-full items-center gap-1.5 text-xs font-medium text-tertiary hover:text-primary transition-colors"
                    >
                        {detailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        System details
                        <span className="text-quaternary">
                            ({attentionReasons.length + systemActions.length} items)
                        </span>
                    </button>

                    {detailsOpen && (
                        <div className="mt-2 space-y-3 pl-5">
                            {/* Attention reasons */}
                            {attentionReasons.length > 0 && (
                                <div className="space-y-1">
                                    {attentionReasons.map((reason, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-500" />
                                            <p className="text-xs text-primary">{reason}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Extracted references */}
                            {refs && (refs.booking_numbers.length > 0 || refs.container_numbers.length > 0 || refs.bl_numbers.length > 0) && (
                                <div className="rounded-md bg-secondary/40 px-3 py-2">
                                    <p className="mb-1 text-[10px] font-medium text-secondary-foreground">
                                        Extracted references
                                        {emailSubject && <span className="font-normal text-quaternary"> — from: {emailSubject.length > 50 ? emailSubject.slice(0, 50) + "…" : emailSubject}</span>}
                                    </p>
                                    <div className="space-y-0.5 text-xs">
                                        {refs.booking_numbers.length > 0 && <p className="text-tertiary">Booking: <span className="font-mono text-primary">{refs.booking_numbers.join(", ")}</span></p>}
                                        {refs.container_numbers.length > 0 && <p className="text-tertiary">Container: <span className="font-mono text-primary">{refs.container_numbers.join(", ")}</span></p>}
                                        {refs.bl_numbers.length > 0 && <p className="text-tertiary">B/L: <span className="font-mono text-primary">{refs.bl_numbers.join(", ")}</span></p>}
                                    </div>
                                    {creation && (
                                        <p className="mt-1 text-[10px] text-tertiary">
                                            Auto-created{creation.container_number_found ? ` (${creation.container_number_found})` : ""} — {creation.attachments_count} attachment(s)
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* System actions timeline */}
                            {systemActions.length > 0 && (
                                <div className="space-y-1">
                                    {systemActions.map((action, i) => (
                                        <div key={i} className="flex items-start gap-2 text-xs">
                                            <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-quaternary" />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-start justify-between gap-2">
                                                    <span className="font-medium text-secondary-foreground">{action.title}</span>
                                                    {action.timestamp && (
                                                        <span className="shrink-0 text-quaternary">
                                                            {new Date(action.timestamp).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" })}{" "}
                                                            {new Date(action.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                        </span>
                                                    )}
                                                </div>
                                                {(action.source || action.email_subject) && (
                                                    <p className="text-quaternary">
                                                        {action.source && (
                                                            <span>
                                                                {"Source: "}
                                                                {action.source === "email_body" ? "Email" : action.source === "jsoncargo_sync" ? "Tracking (JSONCargo)" : action.source === "system_check_missing_data" ? "System: missing critical data" : action.source === "system_check_document_validation" ? "System: document validation" : action.source === "system_check_missing_document" ? "System: missing required document" : action.source === "system_check" ? "System check" : action.source === "auto_detection" ? "System check" : action.source === "coherence_guard" ? "System: status/date coherence" : action.source.replace(/_/g, " ")}
                                                            </span>
                                                        )}
                                                        {action.email_subject && (
                                                            <span className="text-quaternary">
                                                                {action.source ? " — " : ""}
                                                                {action.email_subject.length > 60 ? action.email_subject.slice(0, 60) + "…" : action.email_subject}
                                                            </span>
                                                        )}
                                                    </p>
                                                )}
                                                {action.email_from && (
                                                    <p className="text-quaternary text-[10px]">
                                                        {"From: "}{action.email_from}
                                                    </p>
                                                )}
                                                {action.changes && typeof action.changes === "object" && Object.keys(action.changes).length > 0 && (
                                                    <div className="mt-1 space-y-0.5">
                                                        {Object.entries(action.changes as Record<string, string>).map(([field, change]) => (
                                                            <div key={field} className="flex gap-1 text-[11px]">
                                                                <span className="text-quaternary">{humanizeFieldName(field)}:</span>
                                                                <span className="text-tertiary">{change}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function DocumentDetailPanel({ documentId, onClose }: { documentId: string; onClose: () => void }) {
    const { data: preview, isLoading } = useDocumentPreview(documentId);
    const isPdf = preview?.file_mime_type === "application/pdf" || preview?.file_name?.toLowerCase().endsWith(".pdf");

    // Fetch download URL for PDF iframe
    const { data: downloadData } = useQuery({
        queryKey: ["document-download-url", documentId],
        queryFn: async () => {
            const { apiGet } = await import("@/lib/api-client");
            return apiGet<{ download_url: string; file_name: string }>(`/documents/${documentId}/download`);
        },
        enabled: !!documentId,
        staleTime: 50 * 60 * 1000, // 50 min (signed URLs last 1h)
    });

    const pdfUrl = isPdf && downloadData?.download_url ? downloadData.download_url : null;

    return (
        <div className="flex h-full flex-col">
            {/* Header — compact: filename + type badge + actions */}
            <div className="flex items-center gap-2 border-b border-secondary px-3 py-2">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-primary">{preview?.file_name || "Document"}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-secondary-foreground">
                            {formatDocumentType(preview?.document_type || "unknown")}
                        </span>
                        {preview?.file_size_bytes && (
                            <span className="text-[10px] text-quaternary">{Math.round(preview.file_size_bytes / 1024)} KB</span>
                        )}
                        {preview?.container && (
                            <Link to={`/shipper/containers/${preview.container.container_id}`} className="text-[10px] text-brand-600 hover:text-brand-700">
                                {preview.container.container_number || preview.container.container_id.slice(0, 8)}
                            </Link>
                        )}
                    </div>
                </div>
                <Link
                    to={`/shipper/documents/${documentId}`}
                    target="_blank"
                    className="shrink-0 rounded p-1 text-quaternary transition-colors hover:text-primary"
                    title="Open in full viewer"
                >
                    <LinkExternal01 className="h-4 w-4" />
                </Link>
                <button
                    onClick={onClose}
                    className="shrink-0 rounded p-1 text-quaternary transition-colors hover:text-primary"
                >
                    <XCircle className="h-4 w-4" />
                </button>
            </div>

            {/* Content — PDF takes all available space */}
            {isLoading ? (
                <div className="flex flex-1 items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
                </div>
            ) : pdfUrl ? (
                <iframe
                    src={pdfUrl}
                    title={preview?.file_name || "PDF Preview"}
                    className="min-h-0 flex-1"
                />
            ) : downloadData?.download_url ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
                    <File06 className="h-16 w-16 text-quaternary" />
                    <p className="text-sm text-tertiary">Preview not available for this file type</p>
                    <a
                        href={downloadData.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-lg border border-secondary bg-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-secondary"
                    >
                        <LinkExternal01 className="h-4 w-4" />
                        Download File
                    </a>
                </div>
            ) : (
                <div className="flex flex-1 items-center justify-center">
                    <File06 className="h-16 w-16 text-quaternary" />
                </div>
            )}
        </div>
    );
}

function DocCard({
    doc,
    decision,
    onDecision,
    onOpenPreview,
    moveToContainerId,
    onMoveTargetChange,
}: {
    doc: ReviewDocument;
    decision: string;
    onDecision: (action: "keep" | "reclassify" | "remove" | "move") => void;
    onOpenPreview?: (docId: string) => void;
    moveToContainerId?: string;
    onMoveTargetChange?: (containerId: string) => void;
}) {
    const { data: preview } = useDocumentPreview(doc.document_id);
    const thumbnailUrl = preview?.metadata?.thumbnail_url;
    const [imgError, setImgError] = useState(false);
    const [moveSearch, setMoveSearch] = useState("");
    const [showMoveResults, setShowMoveResults] = useState(false);

    return (
        <div className={`rounded-lg border ${decision === "remove" ? "border-error-200 bg-error-50/50"
                : decision === "move" ? "border-brand-200 bg-brand-50/30"
                    : "border-secondary"
            }`}>
            <div className="flex items-center gap-3 p-3">
                <button
                    onClick={() => onOpenPreview?.(doc.document_id)}
                    className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-secondary bg-secondary/30 hover:border-brand-300 transition-colors"
                >
                    {thumbnailUrl && !imgError ? (
                        <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" onError={() => setImgError(true)} />
                    ) : (
                        <File06 className="h-6 w-6 text-quaternary" />
                    )}
                </button>

                <div className="min-w-0 flex-1">
                    <button
                        onClick={() => onOpenPreview?.(doc.document_id)}
                        className="block truncate text-sm font-medium text-primary hover:text-brand-600 text-left"
                    >
                        {doc.original_filename || doc.document_id.slice(0, 8)}
                    </button>
                    <div className="mt-0.5 flex items-center gap-2">
                        <span className="rounded bg-secondary px-1.5 py-0.5 text-xs text-secondary-foreground">
                            {formatDocumentType(doc.document_type || "unknown")}
                        </span>
                        {doc.extraction_status && (
                            <span className="text-xs text-quaternary">{doc.extraction_status}</span>
                        )}
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                    <button
                        onClick={() => { onDecision("move"); setShowMoveResults(true); }}
                        className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${decision === "move"
                                ? "bg-brand-100 text-brand-700"
                                : "text-tertiary hover:bg-secondary hover:text-primary"
                            }`}
                        title="Move to another container"
                    >
                        <ArrowRight className="h-3.5 w-3.5" />
                        Move
                    </button>
                    <button
                        onClick={() => onDecision(decision === "remove" ? "keep" : "remove")}
                        className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${decision === "remove"
                                ? "bg-error-100 text-error-700"
                                : "text-tertiary hover:bg-secondary hover:text-primary"
                            }`}
                        title="Remove document"
                    >
                        <XCircle className="h-3.5 w-3.5" />
                        Remove
                    </button>
                </div>
            </div>

            {/* Move-to-container search */}
            {decision === "move" && (
                <div className="border-t border-secondary px-3 py-2">
                    <p className="mb-1.5 text-xs font-medium text-tertiary">Move to container:</p>
                    <ContainerSearchInput
                        value={moveSearch}
                        onChange={setMoveSearch}
                        selectedId={moveToContainerId}
                        onSelect={(id, label) => {
                            onMoveTargetChange?.(id);
                            setMoveSearch(label);
                            setShowMoveResults(false);
                        }}
                        showResults={showMoveResults}
                        onFocus={() => setShowMoveResults(true)}
                        onBlur={() => setTimeout(() => setShowMoveResults(false), 200)}
                    />
                </div>
            )}
        </div>
    );
}

function ContainerSearchInput({
    value,
    onChange,
    selectedId,
    onSelect,
    showResults,
    onFocus,
    onBlur,
}: {
    value: string;
    onChange: (v: string) => void;
    selectedId?: string;
    onSelect: (id: string, label: string) => void;
    showResults: boolean;
    onFocus: () => void;
    onBlur: () => void;
}) {
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Debounce search
    useState(() => {
        const timer = setTimeout(() => setDebouncedSearch(value), 300);
        return () => clearTimeout(timer);
    });

    // Simple container search via API
    const { data } = useQuery({
        queryKey: ["container-search-move", debouncedSearch],
        queryFn: async () => {
            if (!debouncedSearch || debouncedSearch.length < 2) return [];
            const { apiGet } = await import("@/lib/api-client");
            const result = await apiGet<{ containers: Array<{ container_id: string; container_number: string | null; booking_number: string | null; status: string | null }> }>(
                "/containers-recap/table",
                { params: { search: debouncedSearch, limit: "5" } }
            );
            return result.containers || [];
        },
        enabled: showResults && debouncedSearch.length >= 2,
        staleTime: 10_000,
    });

    return (
        <div className="relative">
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => { onChange(e.target.value); setDebouncedSearch(e.target.value); }}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder="Search by container or booking number..."
                    className="w-full rounded-md border border-secondary bg-primary px-2.5 py-1.5 text-xs text-primary placeholder:text-quaternary focus:border-brand-500 focus:outline-none"
                />
                {selectedId && (
                    <CheckCircle className="h-4 w-4 shrink-0 text-success-500" />
                )}
            </div>
            {showResults && data && data.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-md border border-secondary bg-primary shadow-lg">
                    {data.map((c) => (
                        <button
                            key={c.container_id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onSelect(c.container_id, c.container_number || c.booking_number || c.container_id.slice(0, 8))}
                            className={`flex w-full items-center justify-between px-2.5 py-1.5 text-xs hover:bg-secondary transition-colors ${selectedId === c.container_id ? "bg-brand-50 text-brand-700" : "text-primary"
                                }`}
                        >
                            <span className="font-medium">{c.container_number || c.booking_number || c.container_id.slice(0, 8)}</span>
                            {c.status && <span className="text-quaternary">{c.status.replace(/_/g, " ")}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export { ReviewCenterPage as ShipperReviewCenterPage };
